import { logger } from '@/lib/logger';

/** Opens the puzzle and calls back once the customer solves it or gives up. */
type ChallengeUi = (settle: (solved: boolean) => void) => void;

/** Mints a fresh reCAPTCHA token. Supplied by React, which owns `executeRecaptcha`. */
type TokenMinter = (action?: string) => Promise<string | undefined>;

/** Opens the dead-end popup once a request has spent its whole challenge budget. */
type TerminalUi = () => void;

let challengeUi: ChallengeUi | null = null;
let tokenMinter: TokenMinter | null = null;
let terminalUi: TerminalUi | null = null;
let pending: Promise<boolean> | null = null;
let settlePending: ((solved: boolean) => void) | null = null;
let terminalEngaged = false;

/**
 * Connects the mounted puzzle modal to the Axios interceptor.
 *
 * The interceptor runs outside React and cannot render anything; the modal lives inside React
 * and cannot be reached from a module-scope function. This tiny registry is the seam. Returns
 * an unregister function for the provider's effect cleanup.
 */
export function registerChallengeUi(ui: ChallengeUi): () => void {
  challengeUi = ui;
  return () => {
    if (challengeUi === ui) challengeUi = null;
  };
}

/**
 * Connects the reCAPTCHA token minter to the interceptor, for the same reason as
 * {@link registerChallengeUi} — `executeRecaptcha` only exists inside `GoogleReCaptchaProvider`.
 */
export function registerTokenMinter(minter: TokenMinter): () => void {
  tokenMinter = minter;
  return () => {
    if (tokenMinter === minter) tokenMinter = null;
  };
}

/**
 * Connects the dead-end popup to the interceptor, for the same reason as
 * {@link registerChallengeUi}. Shown once a request has used up its challenge budget.
 */
export function registerCaptchaTerminalUi(ui: TerminalUi): () => void {
  terminalUi = ui;
  return () => {
    if (terminalUi === ui) terminalUi = null;
  };
}

/**
 * Mints a fresh token, or resolves undefined when no minter is registered (SSR, tests, or a
 * page rendered outside the provider). Callers must treat undefined as "replay without a new
 * token" rather than throwing — a missing minter is a configuration state, not a request error.
 */
export async function mintCaptchaToken(action?: string): Promise<string | undefined> {
  if (!tokenMinter) return undefined;
  return tokenMinter(action);
}

/**
 * Shows the puzzle and resolves with whether the customer solved it.
 *
 * ## Invariants:
 * - Concurrent callers share ONE puzzle. Why: a screen routinely fires several guarded requests
 *   in the same tick — the QR booking page alone asks for the zone, the rate and the garage — so
 *   without coalescing a single bad score stacks three modals and demands three solves for what
 *   is one verification problem. They all await the same promise and all replay off one solve.
 * - Resolves false rather than rejecting when no UI is registered. Why: this runs inside an
 *   Axios interceptor, where a throw would replace the API's own error with an unrelated one and
 *   destroy the diagnosis. No modal mounted means no challenge is possible, which is exactly
 *   "not solved".
 * - The in-flight promise is cleared before the value is handed back, so the next refusal opens
 *   a new puzzle instead of resolving instantly against the settled one.
 * - Resolves false without opening anything once the terminal popup is engaged. Why: that popup
 *   is deliberately non-dismissible, so a puzzle opened behind it would be unreachable — the
 *   customer would face a dead end with an invisible challenge parked behind it. Once we have
 *   told them to reload, no further puzzle can help.
 */
export function requestCaptchaChallenge(): Promise<boolean> {
  if (terminalEngaged) return Promise.resolve(false);
  if (pending) return pending;

  const ui = challengeUi;
  if (!ui) {
    logger.warn('Captcha refused but no challenge UI is mounted — surfacing the original error', {
      feature: 'captcha',
      route: 'requestCaptchaChallenge',
    });
    return Promise.resolve(false);
  }

  const challenge = new Promise<boolean>((resolve) => {
    let settled = false;
    const settle = (solved: boolean) => {
      // The modal is user-driven; a stray second call (double-tap on the solve, a close that
      // races the solve) must not reopen the gate for an already-replayed request.
      if (settled) return;
      settled = true;
      resolve(solved);
    };
    // Held so {@link showCaptchaTerminal} can release a puzzle it is about to cover.
    settlePending = settle;
    ui(settle);
  });

  pending = challenge;
  // Cleared here rather than inside the settle callback: a UI that settles SYNCHRONOUSLY (any
  // test double, and a real modal that auto-passes) would otherwise null `pending` before the
  // assignment below ran, leaving a permanently-resolved promise in the slot — after which every
  // later refusal resolves instantly against the previous answer and no puzzle ever opens again.
  // `finally` always lands a microtask later, so the assignment has happened by then.
  void challenge.finally(() => {
    if (pending === challenge) {
      pending = null;
      settlePending = null;
    }
  });

  return challenge;
}

/**
 * Engages the dead-end popup: verification could not be completed and the page must be reloaded.
 *
 * ## Invariants:
 * - Latches for the life of the page. Why: the only way out of this popup is a reload, so there
 *   is no state to go back to. The latch is what stops a later refusal from opening a puzzle
 *   behind a modal that cannot be closed.
 * - Releases the in-flight puzzle as unsolved. Why: concurrent refusals share ONE puzzle, so the
 *   request that just spent its last attempt is not necessarily the one that puzzle belongs to.
 *   Leaving that promise unsettled would park its request forever behind a modal we have just
 *   covered; resolving false surfaces the API's own refusal, which the call site already handles.
 * - The counter that decides WHEN to call this is per-request (`captchaAttempt` on the Axios
 *   config); only this latch is global. Why: the budget belongs to one request and its replay
 *   chain, but a dead end belongs to the page — every request on it is equally stuck.
 * - A missing UI is logged, not thrown. Why: this is called from inside an Axios interceptor,
 *   where a throw would replace the API's own error with an unrelated one.
 */
export function showCaptchaTerminal(): void {
  terminalEngaged = true;

  if (!terminalUi) {
    logger.warn('Captcha attempts exhausted but no terminal UI is mounted', {
      feature: 'captcha',
      route: 'showCaptchaTerminal',
    });
  } else {
    terminalUi();
  }

  settlePending?.(false);
}

/** Test seam: drops the registry, any in-flight challenge, and the terminal latch. */
export function resetCaptchaBroker(): void {
  challengeUi = null;
  tokenMinter = null;
  terminalUi = null;
  pending = null;
  settlePending = null;
  terminalEngaged = false;
}
